import React, { useState, useRef, useEffect } from 'react';
import * as faceapi from 'face-api.js';
import { resolveApiBaseUrl } from './constants';

/**
 * StudentRegistration - Manual registration flow for students
 * Steps: System Check → ID Upload → Face Capture → Complete
 */
const StudentRegistration = ({ 
  userId, 
  examId, 
  apiBaseUrl,
  modelsBaseUrl = '/models',
  onRegistrationComplete,
  onCancel
}) => {
  const resolvedApiBaseUrl = resolveApiBaseUrl(apiBaseUrl);
  const [step, setStep] = useState(1);
  const [systemCheck, setSystemCheck] = useState({
    browser: false,
    camera: false,
    microphone: false,
    internet: false,
    models: false,
    noise: false,
    lighting: false
  });
  const [idImage, setIdImage] = useState(null);
  const [faceImage, setFaceImage] = useState(null);
  const [cameraStream, setCameraStream] = useState(null);
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [noiseLevel, setNoiseLevel] = useState(0);
  const [lightingLevel, setLightingLevel] = useState(0);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  // Load models
  useEffect(() => {
    const loadModels = async () => {
      try {
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(modelsBaseUrl),
          faceapi.nets.faceLandmark68Net.loadFromUri(modelsBaseUrl),
          faceapi.nets.faceRecognitionNet.loadFromUri(modelsBaseUrl)
        ]);
        setModelsLoaded(true);
        setSystemCheck(prev => ({ ...prev, models: true }));
      } catch (err) {
        console.error('Model loading error:', err);
      }
    };
    loadModels();
  }, [modelsBaseUrl]);

  // System check (Enhanced v0.4.0)
  const runSystemCheck = async () => {
    setLoading(true);
    setError(null);
    const results = { ...systemCheck };

    results.browser = !!navigator.mediaDevices;
    results.internet = navigator.onLine;
    
    // Camera test
    try {
      const vidStream = await navigator.mediaDevices.getUserMedia({ video: true });
      
      // Test lighting quality (v0.4.0)
      if (videoRef.current) {
        videoRef.current.srcObject = vidStream;
        await new Promise(resolve => {
          videoRef.current.onloadedmetadata = () => {
            videoRef.current.play();
            setTimeout(resolve, 500); // Wait for video to stabilize
          };
        });
        
        // Analyze brightness
        const tempCanvas = document.createElement('canvas');
        const ctx = tempCanvas.getContext('2d');
        tempCanvas.width = videoRef.current.videoWidth;
        tempCanvas.height = videoRef.current.videoHeight;
        ctx.drawImage(videoRef.current, 0, 0, tempCanvas.width, tempCanvas.height);
        const imageData = ctx.getImageData(0, 0, tempCanvas.width, tempCanvas.height).data;
        
        let brightness = 0;
        for (let i = 0; i < imageData.length; i += 4) {
          brightness += (imageData[i] * 0.299 + imageData[i+1] * 0.587 + imageData[i+2] * 0.114);
        }
        brightness = Math.round(brightness / (imageData.length / 4));
        setLightingLevel(brightness);
        
        // Good lighting: 50-200 range
        results.lighting = brightness >= 50 && brightness <= 200;
      }
      
      vidStream.getTracks().forEach(t => t.stop());
      results.camera = true;
    } catch { 
      results.camera = false;
      results.lighting = false;
    }
    
    // Microphone and noise test (v0.4.0)
    try {
      const audStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      results.microphone = true;
      
      // Test noise levels
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(audStream);
      microphone.connect(analyser);
      analyser.fftSize = 256;
      
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      
      await new Promise(resolve => setTimeout(resolve, 1000)); // Sample for 1 second
      
      analyser.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b, 0) / bufferLength;
      const decibelLevel = Math.round(average);
      setNoiseLevel(decibelLevel);
      
      // Acceptable noise: < 80dB
      results.noise = decibelLevel < 80;
      
      audioContext.close();
      audStream.getTracks().forEach(t => t.stop());
    } catch { 
      results.microphone = false;
      results.noise = false;
    }
    
    setSystemCheck(results);
    
    // Save system check results to database (v0.4.1)
    if (userId && examId) {
      try {
        await fetch(`${resolvedApiBaseUrl}/api/save-system-check`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId,
            examId,
            checkType: 'registration',
            browser: results.browser,
            camera: results.camera,
            microphone: results.microphone,
            internet: results.internet,
            models: results.models,
            noise: results.noise,
            lighting: results.lighting,
            noiseLevel,
            lightingLevel,
            errorMessage: Object.values(results).every(v => v) ? null : 'Some checks failed'
          })
        });
        console.log('[StudentRegistration] System check results saved to database');
      } catch (err) {
        console.error('[StudentRegistration] Failed to save system check:', err);
      }
    }
    
    setLoading(false);

    if (Object.values(results).every(v => v)) {
      setStep(2);
    } else {
      setError('Please resolve issues before proceeding');
    }
  };

  // ID upload
  const handleIdUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => setIdImage(event.target.result);
    reader.readAsDataURL(file);
  };

  // Proceed to face
  const proceedToFace = async () => {
    if (!idImage) {
      setError('Please upload your ID');
      return;
    }
    setLoading(true);
    try {
      await fetch(`${resolvedApiBaseUrl}/api/upload-id`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, examId, idImage, timestamp: new Date().toISOString() })
      });
      setStep(3);
      startCamera();
    } catch (err) {
      setError('Failed to upload ID');
    } finally {
      setLoading(false);
    }
  };

  // Camera
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480 } });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setCameraStream(stream);
      }
    } catch (err) {
      setError('Camera access failed');
    }
  };

  // Capture
  const captureFace = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    setFaceImage(canvas.toDataURL('image/jpeg', 0.9));
  };

  // Complete
  const complete = async () => {
    if (!faceImage) {
      setError('Please capture your face');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${resolvedApiBaseUrl}/api/register-user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, examId, referenceFace: faceImage, timestamp: new Date().toISOString() })
      });
      const data = await res.json();
      if (cameraStream) cameraStream.getTracks().forEach(t => t.stop());
      setStep(4);
      setTimeout(() => onRegistrationComplete && onRegistrationComplete(data), 2000);
    } catch (err) {
      setError('Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // Styles
  const s = {
    container: { fontFamily: 'system-ui', maxWidth: '800px', margin: '0 auto', padding: '20px' },
    card: { backgroundColor: 'white', padding: '30px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
    button: { padding: '12px 24px', backgroundColor: '#2196F3', color: 'white', border: 'none', borderRadius: '6px', fontSize: '16px', cursor: 'pointer', marginTop: '20px' },
    progress: { display: 'flex', justifyContent: 'space-between', marginBottom: '30px' },
    stepCircle: (active) => ({ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: active ? '#2196F3' : '#ccc', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px', fontWeight: 'bold' }),
    check: (pass) => ({ display: 'flex', alignItems: 'center', padding: '12px', margin: '8px 0', backgroundColor: pass ? '#e8f5e9' : '#ffebee', borderRadius: '4px', borderLeft: `4px solid ${pass ? '#4caf50' : '#f44336'}` })
  };

  return (
    <div style={s.container}>
      {/* Progress */}
      <div style={s.progress}>
        {[{ n: 1, l: 'System Check' }, { n: 2, l: 'Upload ID' }, { n: 3, l: 'Capture Face' }, { n: 4, l: 'Complete' }].map(st => (
          <div key={st.n} style={{ textAlign: 'center', flex: 1 }}>
            <div style={s.stepCircle(step >= st.n)}>{step > st.n ? '✓' : st.n}</div>
            <div style={{ fontSize: '12px', color: step >= st.n ? '#2196F3' : '#999' }}>{st.l}</div>
          </div>
        ))}
      </div>

      <div style={s.card}>
        {/* Step 1: System Check */}
        {step === 1 && (
          <div>
            <h2 style={{ marginTop: 0 }}>🔍 System Check</h2>
            <p style={{ color: '#666' }}>Verify your system meets requirements</p>
            {Object.entries(systemCheck).map(([key, val]) => (
              <div key={key} style={s.check(val)}>
                <span style={{ fontSize: '24px', marginRight: '12px' }}>{val ? '✅' : '❌'}</span>
                <div style={{ flex: 1 }}>
                  <div>{key.charAt(0).toUpperCase() + key.slice(1)}</div>
                  {key === 'noise' && noiseLevel > 0 && (
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      Noise Level: {noiseLevel}dB {val ? '(Good)' : '(Too loud - max 80dB)'}
                    </div>
                  )}
                  {key === 'lighting' && lightingLevel > 0 && (
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      Brightness: {lightingLevel}/255 ({Math.round((lightingLevel / 255) * 100)}%) {val ? '(Good)' : '(Adjust lighting - ideal range: 50-200)'}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {error && <div style={{ padding: '12px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px', marginTop: '16px' }}>⚠️ {error}</div>}
            <button onClick={runSystemCheck} disabled={loading} style={{ ...s.button, opacity: loading ? 0.6 : 1 }}>
              {loading ? 'Checking...' : 'Run System Check'}
            </button>
            <video ref={videoRef} style={{ display: 'none' }} />
          </div>
        )}

        {/* Step 2: ID Upload */}
        {step === 2 && (
          <div>
            <h2 style={{ marginTop: 0 }}>🪪 Upload Your ID</h2>
            <p style={{ color: '#666' }}>Upload government-issued ID</p>
            <div style={{ border: '2px dashed #ccc', borderRadius: '8px', padding: '40px', textAlign: 'center', marginTop: '20px', cursor: 'pointer' }} onClick={() => fileInputRef.current?.click()}>
              {idImage ? (
                <div>
                  <img src={idImage} alt="ID" style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '4px' }} />
                  <p style={{ marginTop: '16px', color: '#4caf50' }}>✅ ID uploaded</p>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📁</div>
                  <p>Click to upload your ID</p>
                </div>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleIdUpload} style={{ display: 'none' }} />
            {error && <div style={{ padding: '12px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px', marginTop: '16px' }}>⚠️ {error}</div>}
            <div style={{ marginTop: '20px', display: 'flex', gap: '12px' }}>
              <button onClick={() => setStep(1)} style={{ ...s.button, backgroundColor: '#f5f5f5', color: '#333', marginTop: 0 }}>← Back</button>
              <button onClick={proceedToFace} disabled={!idImage || loading} style={{ ...s.button, opacity: (!idImage || loading) ? 0.6 : 1, marginTop: 0, flex: 1 }}>
                {loading ? 'Uploading...' : 'Next: Capture Face →'}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Face Capture */}
        {step === 3 && (
          <div>
            <h2 style={{ marginTop: 0 }}>📸 Capture Your Face</h2>
            <p style={{ color: '#666' }}>Position your face in the frame</p>
            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              {!faceImage ? (
                <div>
                  <video ref={videoRef} autoPlay muted style={{ width: '100%', maxWidth: '640px', borderRadius: '8px', border: '2px solid #2196F3' }} />
                  <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#e3f2fd', borderRadius: '4px', textAlign: 'left' }}>
                    <strong>Tips:</strong>
                    <ul style={{ margin: '8px 0', paddingLeft: '20px', fontSize: '14px' }}>
                      <li>Look directly at camera</li>
                      <li>Ensure good lighting</li>
                      <li>Remove glasses</li>
                      <li>Keep neutral expression</li>
                    </ul>
                  </div>
                  <button onClick={captureFace} style={s.button}>📸 Capture Face</button>
                </div>
              ) : (
                <div>
                  <canvas ref={canvasRef} style={{ maxWidth: '100%', borderRadius: '8px', border: '2px solid #4caf50' }} />
                  <p style={{ marginTop: '16px', color: '#4caf50' }}>✅ Face captured</p>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                    <button onClick={() => { setFaceImage(null); startCamera(); }} style={{ ...s.button, backgroundColor: '#f5f5f5', color: '#333' }}>🔄 Retake</button>
                    <button onClick={complete} disabled={loading} style={{ ...s.button, opacity: loading ? 0.6 : 1 }}>
                      {loading ? 'Registering...' : 'Complete Registration →'}
                    </button>
                  </div>
                </div>
              )}
            </div>
            <canvas ref={canvasRef} style={{ display: faceImage ? 'block' : 'none', maxWidth: '100%', margin: '0 auto' }} />
            {error && <div style={{ padding: '12px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px', marginTop: '16px' }}>⚠️ {error}</div>}
          </div>
        )}

        {/* Step 4: Complete */}
        {step === 4 && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>✅</div>
            <h2 style={{ color: '#4caf50', margin: '0 0 16px 0' }}>Registration Complete!</h2>
            <p style={{ color: '#666' }}>You're ready to start your exam</p>
            <div style={{ marginTop: '30px', padding: '20px', backgroundColor: '#e8f5e9', borderRadius: '8px' }}>
              <div style={{ fontSize: '14px', color: '#2e7d32' }}>
                <strong>✓</strong> System check passed<br />
                <strong>✓</strong> ID verified<br />
                <strong>✓</strong> Face registered
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentRegistration;

